import "./consensusActivity.css"
import React, { useEffect, useState, useRef } from "react";
import styled, { css } from "styled-components";
import { Card, ListGroup, Alert } from "@bootstrap-styled/v4";
import { logActivity, LogLevel } from "utils/sentryLogger";
import { VerticalFraction } from "./VerticalFraction";
import { MathJax, HTML_TAG } from "./MathJax";
import { MathMLRenderer } from "./MathMLRenderer";
import { Button } from "react-bootstrap";
import {
  addLeadingZeroIfDot,
  colors,
  constants,
  isCorrectOpenResponse,
  isOpenResponseSolutionEmpty,
  removeTrailingZeros,
  validOpenResponseSolution
} from "utils";
import { removeUnits } from "utils";
import Enlarge from "assets/Enlarge.svg";
import { ImageOverlay } from "./ImageOverlay";
import { PromptFormula, hasFormulaText } from "./PromptFormula";
import VolumeUp from "assets/volume_up.svg";
import { ButtonBubble } from "components/shared/ButtonBubble/ButtonBubble";
import { useClientInfo } from "hooks/useClientInfo";
import { REVIEW_STATE } from "constants/reviewStates";

const OpenResponseWidget = ({
  onORAnswer,
  init_solution,
  disabled,
  isDataStateReset,
  content,
  contentPosition,
  prefix: propPrefix,
  suffix: propSuffix,
  render_type,
  isMathJaxSet,
  keyTerms,
  keyTermsDefinition,
  currentTransitionState
}) => {
  const [solution, setSolution] = useState((init_solution === null || init_solution === undefined)
    ? ""
    : init_solution);
  // Answers are numbers (optional minus and decimal point). "decimal" opens the
  // iPad keyboard on its number layer, which still has "-" and ".". Phones get
  // a bare decimal keypad with no minus, so they keep the default keyboard.
  const { device } = useClientInfo();
  const answerInputMode = device.type === "mobile" ? undefined : "decimal";
  const [isBlinking, setIsBlinking] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const prefix = propPrefix || null;
  const suffix = propSuffix || null;
  const hasAdornment = Boolean(prefix || suffix);

  // Track previous values to detect actual changes vs timer re-renders
  const prevInitSolutionRef = useRef(init_solution);
  const prevTransitionStateRef = useRef(currentTransitionState);
  const inputRef = useRef(null);

  // Put the caret in the answer box as each round opens. preventScroll keeps
  // the landing-at-top behaviour; the box is in view on every layout anyway.
  useEffect(() => {
    if (!disabled) inputRef.current?.focus({ preventScroll: true });
  }, [disabled, currentTransitionState]);

  // Update solution when init_solution prop actually changes to a different value
  // This prevents resetting isSubmitted when timer updates cause re-renders
  useEffect(() => {
    const prevValue = prevInitSolutionRef.current;
    const currentValue = init_solution;

    // Always update the displayed solution
    setSolution((currentValue === null || currentValue === undefined) ? "" : currentValue);

    // Only reset submission state if init_solution actually changed (new question)
    if (prevValue !== currentValue) {
      setIsSubmitted(false);
      setIsSubmissionError(false);
      setIsChanged(false);
      prevInitSolutionRef.current = currentValue;
    }
  }, [init_solution]);

  // Reset submission state when transitioning between phases (e.g., SOLO_TIME → TEAM_TIME)
  // This allows the "Talk it out, then click to submit" bubble to appear in TEAM_TIME
  useEffect(() => {
    const prevState = prevTransitionStateRef.current;
    const currentState = currentTransitionState;

    if (prevState !== currentState) {
      setIsSubmitted(false);
      setIsSubmissionError(false);
      setIsChanged(false);
      prevTransitionStateRef.current = currentState;
    }
  }, [currentTransitionState]);

  const handleClick = async () => {
    if (!isChanged && !isDataStateReset) return;
    const submittedSolution = removeTrailingZeros(solution);
    setSolution(submittedSolution);
    setIsBlinking(true);
    setTimeout(() => setIsBlinking(false), 250);
    setIsChanged(false);

    try {
      await onORAnswer(submittedSolution);
      setIsSubmitted(true);
      setIsSubmissionError(false);
    } catch (error) {
      setIsSubmissionError(true);
      setIsSubmitted(false);
      console.error("Error submitting OR answer:", error);
    }
  };

  const isButtonDisabled = () => {
    // Disable button if solution is invalid (e.g. "" or "-") or incomplete (e.g. "3/" or "4 ")
    return (disabled || isOpenResponseSolutionEmpty(solution) || solution.endsWith("/") || solution.endsWith(" "))
  };

  const buttonStyle = {
    backgroundColor: "white",
    border: `1px solid ${colors.blue500}`,
    borderRadius: "100px",
    color: colors.blue500,
    fontWeight: 500,
    animation: isBlinking ? "blink-animation 0.25s ease-in-out" : "none",
    padding: "12px 24px",
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem'
  };

  let buttonText = "Submit";

  const appliedButtonStyle = () => {
    if (isButtonDisabled()) return { ...buttonStyle, ...disabledButtonStyle };
    if (isSubmissionError && !isDataStateReset) {
      buttonText = "Error";
      return { ...buttonStyle, ...errorButtonStyle };
    }
    if (isChanged && isSubmitted && !isDataStateReset) {
      buttonText = "Resubmit";
      return { ...buttonStyle, ...resubmittedButtonStyle };
    }
    if (isSubmitted && !isDataStateReset) {
      buttonText = "Submitted";
      return { ...buttonStyle, ...submittedButtonStyle };
    }
    return buttonStyle;
  }

  return <>
    <div style={{ justifyContent: "center", display: "flex", gap: "32px", marginTop: "1.5rem" }}>
      {hasAdornment ? (
        <div
          data-testid="answer-box"
          className="answer"
          style={disabled ? { ...adornedAnswerBoxStyle, ...disabledAdornedAnswerBoxStyle } : adornedAnswerBoxStyle}
          onClick={(e) => {
            if (disabled) {
              const rect = e.currentTarget.getBoundingClientRect();
              setTooltipPosition({
                x: rect.left + rect.width / 2,
                y: rect.top - 40
              });
              setTooltipVisible(true);
              setTimeout(() => setTooltipVisible(false), 5000);
              return;
            }
            inputRef.current?.focus();
          }}
        >
          {prefix && (
            <span
              className="answer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginRight: prefix.endsWith(" ") ? 0 : "4px",
                whiteSpace: "pre",
                userSelect: "none",
                pointerEvents: "none",
                lineHeight: 1
              }}
            >
              {renderText(prefix, render_type, isMathJaxSet, keyTerms, keyTermsDefinition)}
            </span>
          )}
          <input
            ref={inputRef}
            className="answer"
            type="text"
            inputMode={answerInputMode}
            aria-label="Answer"
            readOnly={disabled}
            value={solution}
            onChange={(e) => {
              if (disabled) return;
              const value = e.target.value;
              if (validOpenResponseSolution(value)) {
                // We want the user to be able to enter a minus sign, or a trailing dot,
                // but technically it is not a valid solution (yet)
                setSolution(addLeadingZeroIfDot(value));
                setIsChanged(true);
              }
            }}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === "Enter" && !isButtonDisabled()) {
                handleClick();
              }
            }}
            onClick={(e) => {
              if (disabled) {
                // Show tooltip when disabled input is clicked
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top - 40
                });
                setTooltipVisible(true);
                setTimeout(() => setTooltipVisible(false), 5000);
              }
            }}
            style={{
              ...innerInputStyle,
              width: `${Math.max(1, (solution || "").length)}ch`,
              cursor: disabled ? "not-allowed" : "text"
            }}
          />
          {suffix && (
            <span
              className="answer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                marginLeft: suffix.startsWith(" ") ? 0 : "4px",
                whiteSpace: "pre",
                userSelect: "none",
                pointerEvents: "none",
                lineHeight: 1
              }}
            >
              {renderText(suffix, render_type, isMathJaxSet, keyTerms, keyTermsDefinition)}
            </span>
          )}
          <style>{clickStyle}</style>
        </div>
      ) : (
        <>
          {contentPosition === "before" && (
            <div className="answer" style={{ alignSelf: "center" }}>{renderText(content, render_type, isMathJaxSet, keyTerms, keyTermsDefinition)}</div>
          )}
          <input
            ref={inputRef}
            className="answer"
            type="text"
            inputMode={answerInputMode}
            aria-label="Answer"
            readOnly={disabled}
            value={solution}
            onChange={(e) => {
              if (disabled) return;
              const value = e.target.value;
              if (validOpenResponseSolution(value)) {
                // We want the user to be able to enter a minus sign, or a trailing dot,
                // but technically it is not a valid solution (yet)
                setSolution(addLeadingZeroIfDot(value));
                setIsChanged(true);
              }
            }}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === "Enter" && !isButtonDisabled()) {
                handleClick();
              }
            }}
            onClick={(e) => {
              if (disabled) {
                // Show tooltip when disabled input is clicked
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltipPosition({
                  x: rect.left + rect.width / 2,
                  y: rect.top - 40
                });
                setTooltipVisible(true);
                setTimeout(() => setTooltipVisible(false), 5000);
              }
            }}
            style={disabled ? { ...inputStyle, ...disabledInputStyle } : inputStyle}
          />
          <style>{clickStyle}</style>
          {contentPosition !== "before" && (
            <div className="answer" style={{ alignSelf: "center" }}>{renderText(content, render_type, isMathJaxSet, keyTerms, keyTermsDefinition)}</div>
          )}
        </>
      )}

      <ButtonBubble display={currentTransitionState === constants.TEAM_TIME && !isOpenResponseSolutionEmpty(solution) && !solution.endsWith("/") && !solution.endsWith(" ") && !isSubmitted} text="Talk it out, then click to submit">
        <Button
          onClick={(e) => {
            if (disabled) {
              const rect = e.currentTarget.getBoundingClientRect();
              setTooltipPosition({
                x: rect.left + rect.width / 2,
                y: rect.top - 40
              });
              setTooltipVisible(true);
              setTimeout(() => setTooltipVisible(false), 5000);
              return;
            }
            if (isButtonDisabled()) {
              return;
            }
            handleClick();
          }}
          style={{
            ...appliedButtonStyle(),
            ...(disabled ? { cursor: 'not-allowed', opacity: 0.6 } : {})
          }}
          type="submit">
          {isSubmitted && !isDataStateReset && (
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="14" viewBox="0 0 19 14" fill="none">
              <path d="M17.5 1.5L6.5 12.5L1.5 7.5" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {buttonText}
        </Button>
      </ButtonBubble>
    </div>
    {tooltipVisible && (
      <div
        style={{
          position: "fixed",
          left: tooltipPosition.x,
          top: tooltipPosition.y,
          transform: "translateX(-50%)",
          backgroundColor: colors.blue100,
          color: colors.nightlyWoods500,
          padding: "8px 12px",
          borderRadius: "100px",
          fontSize: "14px",
          zIndex: 1000,
          pointerEvents: "none",
          whiteSpace: "nowrap"
        }}
      >
        Not yet! Before you answer, take time to check your work.
      </div>
    )}
  </>
};

const highlightKeyTerms = (text, keyTerms, keyTermsDefinition) => {
  if (!keyTerms || keyTerms.trim() === '') return text;

  // Escape special regex characters in keyTerms
  const escapedKeyTerms = keyTerms.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Create regex for case-insensitive matching
  const regex = new RegExp(`(${escapedKeyTerms})`, 'gi');

  // Replace matches with highlighted spans that include tooltip functionality
  return text.replace(regex, (match) => {
    const tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`;
    return `<span
      style="color: ${colors.pink800}; font-weight: 700; position: relative; cursor: help;"
      onmouseenter="showTooltip('${tooltipId}')"
      onmouseleave="hideTooltip('${tooltipId}')"
    >
      ${match}
      <span
        id="${tooltipId}"
        style="
          visibility: hidden;
          position: absolute;
          top: 125%;
          left: 50%;
          transform: translateX(-50%);
          background-color: ${colors.blue100};
          color: ${colors.nightlyWoods950};
          padding: 12px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
          z-index: 1000;
          min-width: 300px;
          max-width: 300px;
          white-space: normal;
          word-wrap: break-word;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        "
      >
        <span style="
          content: '';
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 8px solid #EAEAFC;
        "></span>
        ${keyTermsDefinition || "No definition available"}
      </span>
    </span>`;
  });
};

// Add global functions for tooltip functionality
if (typeof window !== 'undefined') {
  window.showTooltip = (tooltipId) => {
    const tooltip = document.getElementById(tooltipId);
    if (tooltip) {
      tooltip.style.visibility = 'visible';
    }
  };

  window.hideTooltip = (tooltipId) => {
    const tooltip = document.getElementById(tooltipId);
    if (tooltip) {
      tooltip.style.visibility = 'hidden';
    }
  };
}

const renderText = (text, render_type, isMathJaxSet, keyTerms, keyTermsDefinition) => {
  if (text === null || text === undefined) return <></>;
  if (render_type === 'math') {
    // Preserve real line breaks (newline char, 0x0A) so multi-line prompts and
    // bullet-style lists don't collapse into one paragraph. We split on the actual
    // newline character (NOT the literal "\n" escape sequence), so LaTeX commands
    // like \ne / \neq / \newline are never affected — they contain a backslash+n,
    // not a newline byte. MathJax still typesets the \(...\) segments within each line.
    if (typeof text === 'string' && text.includes('\n') && !HTML_TAG.test(text)) {
      const lines = text.split('\n');
      const expression = lines.map((line, i) => (
        <React.Fragment key={i}>
          {line}
          {i < lines.length - 1 && <br />}
        </React.Fragment>
      ));
      return <MathJax expression={expression} isMathJaxSet={isMathJaxSet} />;
    }
    return <MathJax expression={text} isMathJaxSet={isMathJaxSet} />;
  } else {
    const containsHTML = /<[^>]*>/g.test(text);
    if (containsHTML) {
      // Use MathMLRenderer for content that contains mlongdiv or mspace with linebreak
      // This preserves existing behavior for other HTML content
      const containsLongDiv = /<mlongdiv[^>]*>/i.test(text);
      const containsLinebreak = /<mspace[^>]*linebreak\s*=\s*["']newline["'][^>]*>/i.test(text);
      if (containsLongDiv || containsLinebreak) {
        return <MathMLRenderer htmlContent={text} />;
      } else {
        // Use original behavior for other HTML content
        let processedHTML = text.replace(/\\n/g, '');

        // Apply keyword highlighting if keyTerms is provided
        if (keyTerms && keyTerms.trim() !== '') {
          processedHTML = highlightKeyTerms(processedHTML, keyTerms, keyTermsDefinition);
        }

        return <div dangerouslySetInnerHTML={{ __html: processedHTML }} />;
      }
    } else {
      // For plain text, apply highlighting and then handle multiline
      let processedText = text;
      if (keyTerms && keyTerms.trim() !== '') {
        processedText = highlightKeyTerms(text, keyTerms, keyTermsDefinition);
        // If highlighting was applied, render as HTML
        return <div dangerouslySetInnerHTML={{ __html: processedText }} />;
      }
      return <>{handleMultiLine(processedText)}</>;
    }
  }
}

const EXPECTED_SOURCE = "ASSISTMENTS_ENGAGENY_EUREKA_MATH";

const getResponsesForOption = (studentResponses, optionIndex) => {
  if (!studentResponses) return [];
  return Object.values(studentResponses).filter(
    response => response.option_index === optionIndex
  );
};

const getCurrentUserSelectedIndex = (studentResponses, currentUserId) => {
  if (!studentResponses || !currentUserId) return null;
  const userResponse = Object.values(studentResponses).find(
    response => response.oko_user_id === currentUserId
  );
  return userResponse?.option_index ?? null;
};

const groupResponsesBySolution = (responses) => {
  if (!responses) return [];
  const grouped = {};
  Object.values(responses).forEach(response => {
    const solution = response.solution;
    if (solution === null || solution === undefined) return;
    if (!grouped[solution]) {
      grouped[solution] = [];
    }
    grouped[solution].push(response);
  });
  return Object.entries(grouped).map(([solution, resps]) => ({ solution, responses: resps }));
};

const getGroupedOpenResponses = (studentResponses, correctSolution) => {
  const result = groupResponsesBySolution(studentResponses);

  // Sort: correct answer first, then others
  return result.sort((a, b) => {
    const aIsCorrect = isCorrectOpenResponse(a.solution, correctSolution);
    const bIsCorrect = isCorrectOpenResponse(b.solution, correctSolution);
    if (aIsCorrect && !bIsCorrect) return -1;
    if (!aIsCorrect && bIsCorrect) return 1;
    return 0;
  });
};

export const ConsensusActivityRender = ({
  hideQuestion,
  selectedIndex,
  challenge,
  disableOptions,
  onMCAnswer,
  onORAnswer,
  isMathJaxSet,
  isShowTransitionView,
  keyTerms,
  keyTermsDefinition,
  dispatchSpeakPromptRequest,
  isSpokenPromptUsed,
  showBorder = false,
  mcSubmissionError,
  setMcSubmissionError,
  currentTransitionState,
  sessionId,
  studentResponses,
  previousRoundResponses,
  currentUserId,
  // In-session layout only (set by ConsensusActivity): content-sized answer
  // buttons sharing the longest answer's width, and in-flow wrapping response
  // dots. The teacher/preview surfaces that embed this renderer in narrow
  // cards leave it unset and render exactly as before.
  sessionLayout = false,
  // Student session only (set by ConsensusActivity): a question that carries
  // formula text shows it typeset in place of its image.
  showPromptFormula = false,
  // Review mode: the "Review Student Results" overlay's presentation of one
  // student's recorded answer. When absent, rendering is unchanged.
  //   { showAnswers, optionStates: [REVIEW_STATE per option], answerState,
  //     notice: a sentence said inside the card, withheld: draw only that }
  review = null
}) => {
  const [isShowImageNote, setIsShowImageNote] = useState();
  const [isShowImageViewer, setIsShowImageViewer] = useState();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const alertedChallengesRef = useRef(new Set());

  // Check for unexpected question sources and report to Sentry
  useEffect(() => {
    if (challenge?.source && challenge?.id) {
      if (challenge.source !== EXPECTED_SOURCE && !alertedChallengesRef.current.has(challenge.id)) {
        alertedChallengesRef.current.add(challenge.id);
        logActivity("Unexpected Question Source Detected", {
          level: LogLevel.WARNING,
          sessionId: sessionId,
          activityType: "consensus",
          questionId: challenge.id,
          source: challenge.source,
          externalId: challenge.external_id,
          prompt: challenge.spoken_prompt || challenge.prompt?.substring(0, 100),
        });
      }
    }
  }, [challenge?.id, challenge?.source, sessionId]);

  const renderPromptImage = (url) => {

    if (!url || url === 'nan') {
      return null;
    }

    const handleLoad = ({ target: img }) => {
      if (img.naturalWidth > 280 || img.naturalHeight > 400) {
        setIsShowImageNote(true);
      }
    };

    const handleImageActivate = () => {
      if (isShowImageNote) {
        setIsShowImageViewer(prevState => !prevState);
      }
    };

    return (
      <div style={{
        cursor: isShowImageNote ? "pointer" : "default",
        // The pixel ceiling lives on the wrapper, not the img: the render is embedded in
        // containers far narrower than the session's (the Question Set grid card is ~400px),
        // where a fixed cap on the image itself spills out of the card on both sides.
        maxWidth: "680px"
      }}
        role={isShowImageNote ? "button" : undefined}
        tabIndex={isShowImageNote ? 0 : undefined}
        aria-label={isShowImageNote ? "Enlarge question image" : undefined}
        onClick={handleImageActivate}
        onKeyDown={(e) => {
          if (isShowImageNote && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            handleImageActivate();
          }
        }}>
        <img
          src={url}
          onLoad={handleLoad}
          style={{
            display: "block",
            margin: "0 auto",
            maxHeight: "280px",
            maxWidth: "100%"
          }}
          alt="Question visual"
        />
        {isShowImageNote && (
          <div aria-hidden="true" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px" }}>
            <span style={{ fontSize: "10px" }}>Click image to enlarge</span>
            <img src={Enlarge} style={{ margin: "2px" }} alt="" />
          </div>
        )}
      </div>
    );
  };

  // A review reads the session's stored question text, which is plain text with
  // no answer-box marker, so splitting it would guess where the box went and
  // guess wrong. The review shows the question whole, with the answer below it.
  const explicitPrefix =
    typeof challenge?.answer_prefix === "string" && challenge.answer_prefix.trim() !== ""
      ? challenge.answer_prefix
      : null;
  const explicitSuffix =
    typeof challenge?.answer_suffix === "string" && challenge.answer_suffix.trim() !== ""
      ? challenge.answer_suffix
      : typeof challenge?.answer_unit === "string" && challenge.answer_unit.trim() !== ""
      ? challenge.answer_unit
      : null;

  const hasExplicitAdornment = Boolean(explicitPrefix || explicitSuffix);

  let contentResult;
  if (challenge?.answer_options) {
    contentResult = { prompt: challenge?.prompt, prefix: null, suffix: null, content: null, contentPosition: null };
  } else if (hasExplicitAdornment) {
    contentResult = {
      prompt: challenge?.prompt,
      prefix: explicitPrefix,
      suffix: explicitSuffix,
      content: explicitSuffix || explicitPrefix,
      contentPosition: explicitPrefix && !explicitSuffix ? "before" : "after",
    };
  } else if (review) {
    contentResult = { prompt: challenge?.prompt, prefix: null, suffix: null, content: null, contentPosition: null };
  } else {
    const scraped = removeUnits(challenge?.prompt) || { prompt: challenge?.prompt };
    contentResult = {
      prompt: scraped.prompt,
      prefix: null,
      suffix: null,
      content: scraped.content,
      contentPosition: scraped.contentPosition,
    };
  }

  const processedPrompt = contentResult?.prompt ?? challenge?.prompt;

  // Close the image viewer when the transition overlay covers the screen, and
  // also whenever the question changes — the Solo Time overlay is suppressed
  // between questions, so it can no longer be relied on to clear a viewer the
  // student left open on the previous challenge.
  useEffect(() => {
    if (isShowTransitionView) {
      setIsShowImageViewer(false);
    }
  }, [isShowTransitionView]);

  useEffect(() => {
    setIsShowImageViewer(false);
  }, [challenge?.id]);

  const imageUrl = challenge.internal_image_url ?? challenge.prompt_image_url;

  // Review mode never reads aloud: there is no speaker agent in a report.
  const hasSpokenPrompt = !review && process.env.REACT_APP_SPOKEN_PROMPT_ENABLED === "true" &&
    !!dispatchSpeakPromptRequest && !!challenge?.spoken_prompt;

  // A question the overlay cannot show still keeps its card, which says why.
  if (review?.withheld) {
    return (
      <ReviewCard>
        <ReviewNotice role="status">{review.notice}</ReviewNotice>
      </ReviewCard>
    );
  }

  return (
    <>
      {!hideQuestion && (
        <QuestionFrame review={review} style={{
          justifyItems: "center",
          width: "100%",
          maxWidth: "761px",
          /* The read-aloud icon gets a gutter INSIDE the box: hanging it
             outside (the old -44px overhang past 24px padding) let the
             .fullscreen overflow clip it. The gutter stays after the icon
             is used up so the text doesn't jump. */
          padding: hasSpokenPrompt ? "58px 24px 24px 68px" : "58px 24px 24px 24px",
          ...(showBorder && { background: "white", border: `1px solid ${colors.gray700}`, borderRadius: "6px" })
        }}>
          {hasSpokenPrompt && !isSpokenPromptUsed &&
            (
              <Button
                onClick={dispatchSpeakPromptRequest}
                aria-label="Read question aloud"
                style={{
                  position: "absolute",
                  marginLeft: "-44px",
                  backgroundColor: "white",
                  border: "none",
                  borderRadius: "100px",
                  color: colors.nightlyWoods950,
                  cursor: "pointer",
                  height: "32px",
                  width: "32px",
                  padding: 0
                }}
              >
                <img src={VolumeUp} alt="" />
              </Button>

            )
          }
          <div className="prompt" role="heading" aria-level="2" style={review ? { whiteSpace: "pre-line" } : undefined}>
            {renderText(processedPrompt, challenge?.render_type, isMathJaxSet, keyTerms, keyTermsDefinition)}
          </div>
          {mcSubmissionError && (
            <Alert
              variant="danger"
              onClose={() => setMcSubmissionError(null)}
              dismissible
              style={{ marginTop: "16px" }}
            >
              {mcSubmissionError}
            </Alert>
          )}
          {showPromptFormula && hasFormulaText(challenge.prompt_image_latex) ? (
            <PromptFormula latex={challenge.prompt_image_latex} fallback={renderPromptImage(imageUrl)} />
          ) : renderPromptImage(imageUrl)}
          {isShowImageViewer &&
            <ImageOverlay
              imageUrl={imageUrl}
              onClose={() => setIsShowImageViewer(false)}
            />
          }
          {challenge?.answer_options ?
            <AnswerList $sessionLayout={sessionLayout} $review={Boolean(review)}>
              {(() => {
                const currentUserSelectedIndex = getCurrentUserSelectedIndex(studentResponses, currentUserId);
                return challenge.answer_options.map((option, index) => {
                  // Calculate opacity: in SOLUTION phase (studentResponses present),
                  // show correct answer and current user's selection at full opacity, others at 30%
                  const getOptionOpacity = () => {
                    if (!option.eligible) return 0.3;
                    if (studentResponses && currentUserSelectedIndex !== null) {
                      const isCorrectAnswer = challenge.solution_index === index;
                      const isUserSelection = index === currentUserSelectedIndex;
                      return isCorrectAnswer || isUserSelection ? 1 : 0.3;
                    }
                    return 1;
                  };

                  // Check if option is deactivated in SOLUTION phase
                  const isDeactivatedInSolution = !option.eligible && studentResponses;

                  const handleOptionActivate = (e) => {
                    if (disableOptions) {
                      // Show tooltip when disabled option is activated
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltipPosition({
                        x: rect.left + rect.width / 2,
                        y: rect.top - 40
                      });
                      setTooltipVisible(true);
                      setTimeout(() => setTooltipVisible(false), 5000);
                      return;
                    }
                    onMCAnswer(option, index);
                  };

                  return (
                    <OptionRow key={index}>
                      <Option
                        role="button"
                        tabIndex={0}
                        aria-label={`Option ${numberToLetters(index)}: ${option.text}`}
                        aria-disabled={!option.eligible || disableOptions}
                        aria-pressed={selectedIndex === index}
                        $review={Boolean(review)}
                        className={review ? `d-flex review-state-${reviewStateOf(review, index)}` :
                          `w-100 d-flex
                    ${!option.eligible || disableOptions ? "disabled" : ""}
                    ${selectedIndex === index ? "selected" : ""}
                    ${challenge.solution_index === index && option.eligible ? "correct" : ""}
                    ${studentResponses && challenge.solution_index !== index && option.eligible ? "incorrect" : ""}
                    ${isDeactivatedInSolution ? "deactivated" : ""}`
                        }
                        style={{ opacity: getOptionOpacity() }}
                        onClick={handleOptionActivate}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleOptionActivate(e);
                          }
                        }}
                      >
                        <div aria-hidden="true">
                          <span key={index + option.id + "_number"} style={review ? undefined : { fontSize: 18, fontWeight: 700, marginRight: "0.5rem" }}>
                            {numberToLetters(index)})
                          </span>
                          <span className="answer" key={index + option.id + "_option"}>
                            {challenge.render_type === 'math' ?
                              (
                                <MathJax expression={option.text} isMathJaxSet={isMathJaxSet} />
                              ) : (
                                <>
                                  {handleVerticalFractions(option.text)}
                                </>
                              )}
                          </span>
                        </div>
                      </Option>
                      {/* In session layout the container renders on every row of a
                          dots phase (empty rows included), so each row reserves the
                          same dot width and the buttons keep one shared width. */}
                      {/* Review mode draws no dot: the pill's own state is the mark. */}
                      {!review && studentResponses && (sessionLayout || getResponsesForOption(studentResponses, index).length > 0) && (
                        <ResponseDotsContainer $sessionLayout={sessionLayout}>
                          {getResponsesForOption(studentResponses, index).map((response, i) => (
                            <ResponseDot key={response.oko_user_id || i} $isCorrect={challenge.solution_index === index} />
                          ))}
                        </ResponseDotsContainer>
                      )}
                      {!studentResponses && previousRoundResponses && (sessionLayout || getResponsesForOption(previousRoundResponses, index).length > 0) && (
                        <ResponseDotsContainer $sessionLayout={sessionLayout}>
                          {getResponsesForOption(previousRoundResponses, index).map((response, i) => (
                            <ResponseDot key={response.oko_user_id || i} $color={colors.blue500} />
                          ))}
                        </ResponseDotsContainer>
                      )}
                    </OptionRow>
                  );
                });
              })()}
            </AnswerList> : (
              studentResponses || review ? (
                <OpenResponseSolutionView
                  review={review}
                  studentResponses={studentResponses}
                  correctSolution={challenge?.solution}
                  content={contentResult.content}
                  contentPosition={contentResult.contentPosition}
                  render_type={challenge?.render_type}
                  isMathJaxSet={isMathJaxSet}
                  keyTerms={keyTerms}
                  keyTermsDefinition={keyTermsDefinition}
                />
              ) : (
                <OpenResponseWidget
                  init_solution={challenge?.solution}
                  onORAnswer={onORAnswer}
                  disabled={disableOptions}
                  isDataStateReset={selectedIndex === null}
                  content={contentResult.content}
                  contentPosition={contentResult.contentPosition}
                  prefix={contentResult.prefix}
                  suffix={contentResult.suffix}
                  render_type={challenge?.render_type}
                  isMathJaxSet={isMathJaxSet}
                  keyTerms={keyTerms}
                  keyTermsDefinition={keyTermsDefinition}
                  currentTransitionState={currentTransitionState}
                />
              )
            )}
          {challenge?.source === EXPECTED_SOURCE && (
            <div style={{
              width: "100%",
              textAlign: "left",
              fontSize: "12px",
              color: colors.nightlyWoods500,
              fontWeight: 400,
              marginTop: "2rem"
            }}>
              Question Source: ASSISTments
            </div>
          )}
          {review?.notice && <ReviewNotice role="status">{review.notice}</ReviewNotice>}
        </QuestionFrame>
      )}
      {tooltipVisible && (
        <div
          style={{
            position: "fixed",
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: "translateX(-50%)",
            backgroundColor: colors.blue100,
            color: colors.nightlyWoods500,
            padding: "8px 12px",
            borderRadius: "100px",
            fontSize: "14px",
            zIndex: 1000,
            pointerEvents: "none",
            whiteSpace: "nowrap"
          }}
        >
          Not yet! Before you answer, take time to check your work.
        </div>
      )}
    </>
  );
};

const Option = styled(Card)`
  border: 1px solid ${colors.blue500} !important;
  border-radius: 100px !important;
  color: ${colors.blue500};
  cursor: pointer;
  font-size: 1.3rem;
  flex-direction: row !important;
  align-items: center !important;
  background: white !important;
  padding: 8px 24px !important;

  /* Touch screens keep :hover on after a tap, so a reset selection looked
     like a stuck light purple state on iPad. Only fill on real pointers. */
  @media (hover: hover) {
    &:hover {
      background: ${colors.blue200} !important;
    }
  }

  &.disabled {
    cursor: not-allowed;
    background: ${colors.nightlyWoods100} !important;
  }
  
  &.selected {
    background: ${colors.blue500} !important;
    border: 1px solid ${colors.blue500} !important;
    color: ${colors.milkGlass500} !important;
  }

  &.correct {
    background: #007A3F !important;
    border: 1px solid #007A3F !important;
    color: white !important;
  }

  &.incorrect {
    border: 1px solid #FE1E7E !important;
  }

  &.deactivated {
    background: #D5D3CE !important;
    border: 1px solid #D5D3CE !important;
    color: white !important;
  }

  ${({ $review }) => $review && css`
    /* A minimum height, so a stacked fraction grows the pill instead of being
       clipped. */
    width: 250px;
    min-height: 43px;
    padding: 0 16px !important;
    border-radius: 9999px !important;
    cursor: default;
    font-family: "Inter", sans-serif;
    font-size: 18px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: 0.25px;

    & > div {
      display: flex;
      gap: 10px;
      padding: 0 4px;
      align-items: center;
    }

    & .answer {
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
    }

    &.review-state-${REVIEW_STATE.CORRECT} {
      background: ${colors.green600} !important;
      border: none !important;
      color: ${colors.milkGlass500} !important;
    }

    /* The bordered states stroke inside the pill, as the frame does, so the
       label sits where it does in a borderless pill. */
    &.review-state-${REVIEW_STATE.HERS_INCORRECT} {
      background: white !important;
      border: none !important;
      box-shadow: inset 0 0 0 1px ${colors.pink500} !important;
      color: ${colors.pink700} !important;
    }

    &.review-state-${REVIEW_STATE.OTHER} {
      background: ${colors.gray700} !important;
      border: none !important;
      color: ${colors.milkGlass500} !important;
    }

    &.review-state-${REVIEW_STATE.NEUTRAL} {
      background: white !important;
      border: none !important;
      box-shadow: inset 0 0 0 1px ${colors.nightlyWoods500} !important;
      color: ${colors.nightlyWoods500} !important;
    }
  `}
`;

const QuestionFrame = ({ review, style, children }) =>
  review ? <ReviewCard>{children}</ReviewCard> : <div style={style}>{children}</div>;

const ReviewCard = styled.div`
  align-items: center;
  background: white;
  border: 1px solid ${colors.gray700};
  border-radius: 6px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 100%;
  padding: 16px;
  width: 560px;

  & > .prompt {
    align-self: stretch;
    color: ${colors.nightlyWoods500};
    font-family: "Inter", sans-serif;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 0.25px;
    line-height: 22px;
    padding-bottom: 0;
    text-align: left;
  }
`;

const ReviewNotice = styled.p`
  align-self: stretch;
  color: ${colors.nightlyWoods500};
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 22px;
  margin: 0;
  text-align: left;
`;

// With Show Answers unchecked every option is neutral.
const reviewStateOf = (review, index) =>
  review.showAnswers ? review.optionStates[index] : REVIEW_STATE.NEUTRAL;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

// Per the tablet spec, the in-session answer set is content-sized: the whole
// list takes the width of the longest answer (min 250px), capped at the column
// width so long answers wrap and the pill grows. Buttons are w-100 of this
// container, which is what keeps every button in the set the same width.
// fit-content, not max-content: max-content also makes the longest unwrapped
// answer the intrinsic minimum of every ancestor (the percentage cap is
// ignored during intrinsic sizing), which pushed the whole column past the
// viewport instead of wrapping the answer.
const AnswerList = styled(ListGroup)`
  border: none;
  gap: 16px;
  min-width: 240px;
  justify-self: center;
  margin-top: 34px;

  ${({ $sessionLayout }) => $sessionLayout && css`
    width: fit-content;
    min-width: 250px;
    max-width: 100%;
    justify-self: auto;
    margin: 34px auto 0;

    ${Option} {
      min-width: 0;
    }
  `}

  ${({ $review }) => $review && css`
    width: 250px;
    min-width: 0;
    margin-top: 0;
  `}
`;

const ResponseDotsContainer = styled.div`
  display: flex;
  gap: 4px;
  position: absolute;
  left: 100%;
  padding-left: 8px;

  /* In-flow in the session layout: a fixed 50px reserve fits 3 dots per row
     (3×14 + 2×4) and wraps extras; when the column runs out of room the
     button shrinks and its text wraps instead of the dots leaving the
     viewport. */
  ${({ $sessionLayout }) => $sessionLayout && css`
    position: static;
    flex-wrap: wrap;
    /* Three 14px dots + two 4px gaps, plus this box's 8px left padding
       (border-box) — anything narrower wraps at two dots. */
    width: 58px;
    flex-shrink: 0;
    align-content: center;
  `}
`;

const ResponseDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${({ $color, $isCorrect }) => $color || ($isCorrect ? "#007A3F" : "#FE1E7E")};
`;

const OpenResponseSolutionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 1.5rem;
  align-items: center;

  ${({ $review }) => $review && css`
    margin-top: 0;
  `}
`;

const OpenResponseSolutionRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const OpenResponseSolutionInput = styled.div`
  width: 150px;
  height: 50px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  background-color: ${({ $isCorrect }) => $isCorrect ? '#007A3F' : 'white'};
  border: 1px solid ${({ $isCorrect }) => $isCorrect ? '#007A3F' : '#FE1E7E'};
  color: ${({ $isCorrect }) => $isCorrect ? 'white' : 'inherit'};

  ${({ $review }) => $review && css`
    width: 250px;
    height: auto;
    min-height: 43px;
    padding: 0 16px;
    border-radius: 9999px;
    font-family: "Inter", sans-serif;
    font-size: 18px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: 0.25px;

    &.review-state-${REVIEW_STATE.CORRECT} {
      background: ${colors.green600};
      border: none;
      color: ${colors.milkGlass500};
    }

    &.review-state-${REVIEW_STATE.HERS_INCORRECT} {
      background: white;
      border: none;
      box-shadow: inset 0 0 0 1px ${colors.pink500};
      color: ${colors.pink700};
    }

    &.review-state-${REVIEW_STATE.NEUTRAL} {
      background: white;
      border: none;
      box-shadow: inset 0 0 0 1px ${colors.nightlyWoods500};
      color: ${colors.nightlyWoods500};
    }
  `}
`;

export const NO_ANSWER_RECORDED = "No answer recorded";

// The student's words verbatim in the verdict's state, with the key below when
// the answer was wrong and answers are shown. The question's unit or other
// content sits before or after each box, where the session draws it. With no
// recorded answer, a neutral box says so, and the key shows with the answers:
// the live input is never drawn in a review.
const ReviewOpenResponse = ({ review, studentResponses, correctSolution, beside }) => {
  const answer = studentResponses ? Object.values(studentResponses)[0] : null;
  const hasKey = correctSolution !== null && correctSolution !== undefined;
  const answerState = answer && review.showAnswers ? review.answerState : REVIEW_STATE.NEUTRAL;
  const showKey = review.showAnswers && hasKey &&
    (!answer || review.answerState === REVIEW_STATE.HERS_INCORRECT);
  const answerBox = (
    <OpenResponseSolutionInput $review className={`review-state-${answerState}`}>
      {answer ? answer.solution : NO_ANSWER_RECORDED}
    </OpenResponseSolutionInput>
  );
  return (
    <OpenResponseSolutionContainer $review>
      <OpenResponseSolutionRow>{answer ? beside(answerBox) : answerBox}</OpenResponseSolutionRow>
      {showKey && (
        <OpenResponseSolutionRow>
          {beside(
            <OpenResponseSolutionInput $review className={`review-state-${REVIEW_STATE.CORRECT}`}>
              {correctSolution}
            </OpenResponseSolutionInput>
          )}
        </OpenResponseSolutionRow>
      )}
    </OpenResponseSolutionContainer>
  );
};

const OpenResponseSolutionView = ({
  review,
  studentResponses,
  correctSolution,
  content,
  contentPosition,
  render_type,
  isMathJaxSet,
  keyTerms,
  keyTermsDefinition
}) => {
  // A box with the question's content before or after it.
  const beside = (box) => (
    <div style={{ display: "flex", gap: "32px", alignItems: "center" }}>
      {contentPosition === "before" && content && (
        <div className="answer" style={{ alignSelf: "center" }}>
          {renderText(content, render_type, isMathJaxSet, keyTerms, keyTermsDefinition)}
        </div>
      )}
      {box}
      {contentPosition !== "before" && content && (
        <div className="answer" style={{ alignSelf: "center" }}>
          {renderText(content, render_type, isMathJaxSet, keyTerms, keyTermsDefinition)}
        </div>
      )}
    </div>
  );

  if (review) {
    return (
      <ReviewOpenResponse
        review={review}
        studentResponses={studentResponses}
        correctSolution={correctSolution}
        beside={beside}
      />
    );
  }

  const groupedResponses = getGroupedOpenResponses(studentResponses, correctSolution);
  const hasCorrectInResponses = correctSolution != null &&
    groupedResponses.some(({ solution }) => isCorrectOpenResponse(solution, correctSolution));

  if (groupedResponses.length === 0 && !correctSolution) {
    return null;
  }

  return (
    <OpenResponseSolutionContainer>
      {!hasCorrectInResponses && correctSolution != null && (
        <OpenResponseSolutionRow key="correct-solution">
          {beside(
            <OpenResponseSolutionInput $isCorrect={true}>
              {correctSolution}
            </OpenResponseSolutionInput>
          )}
        </OpenResponseSolutionRow>
      )}
      {groupedResponses.map(({ solution, responses }) => {
        const isCorrect = isCorrectOpenResponse(solution, correctSolution);
        return (
          <OpenResponseSolutionRow key={solution}>
            {beside(
              <OpenResponseSolutionInput $isCorrect={isCorrect}>
                {solution}
              </OpenResponseSolutionInput>
            )}
            <ResponseDotsContainer>
              {responses.map((response, i) => (
                <ResponseDot key={response.oko_user_id || i} $isCorrect={isCorrect} />
              ))}
            </ResponseDotsContainer>
          </OpenResponseSolutionRow>
        );
      })}
    </OpenResponseSolutionContainer>
  );
};

const numberToLetters = (number) => {
  var abc = "abcdefghijklmnopqrstuvwxyz";
  var abcLength = abc.length;
  var converted = "";

  while (number >= 0) {
    converted = abc.charAt(number % abcLength) + converted;
    number = Math.floor(number / abcLength) - 1;
  }

  return converted;
};

const handleMultiLine = (text) => {
  const newLine = '\\n'
  if (!text.includes(newLine)) return handleVerticalFractions(text);

  return text.split(newLine).map(
    (line, index) => (
      <React.Fragment key={index}>
        <div>{handleVerticalFractions(line)}</div>
        <br />
      </React.Fragment>
    ))
};

const handleVerticalFractions = (text) => {
  if (!text) return text;
  text = text.replace(/\u00a0/g, " "); // this replaces all NBSP characters with space
  if (text.indexOf("/") < 1) return text; // also handling a case where "/" is at index 0 - not a valid fraction
  const words = text.split(" ");
  let insertSpaceAfterFraction = false;
  for (let i = 0; i < words.length; i++) {
    if (words[i].indexOf("/") > 0) { // word with "/" at index 0 is not a valid fraction
      let top = words[i].split("/")[0];
      let charsBeforeFraction = "";
      // Handling a case where there is non-number char in the top, like opening parenthesis
      for (let j = 0; j < top.length; j++) {
        if (!isNumber(top.charAt(j))) {
          charsBeforeFraction = charsBeforeFraction + top.charAt(j);
        } else {
          break;
        }
      }
      top = top.slice(charsBeforeFraction.length);
      let bottom = words[i].split("/")[1];
      let charsAfterFraction = "";
      // Handling a case where there is no space after the fraction and non-number char goes next like "1/8?"
      for (let j = 0; j < bottom.length; j++) {
        if (!isNumber(bottom.charAt(j))) {
          charsAfterFraction = bottom.substring(j, bottom.length);
          bottom = bottom.substring(0, j);
          break;
        }
      }
      words[i] = <span>
        {charsBeforeFraction && <span>{charsBeforeFraction}</span>}
        <VerticalFraction key={i} numerator={top} denominator={bottom} />
        {charsAfterFraction && <span>{charsAfterFraction}</span>}
      </span>;
      insertSpaceAfterFraction = true;
    } else {
      if (insertSpaceAfterFraction) {
        words[i] = <span key={i}>{" " + words[i] + " "}</span>;
      } else {
        words[i] = <span key={i}>{words[i] + " "}</span>;
      }
      insertSpaceAfterFraction = false;
    }
  }
  return words;
};

const isNumber = (str) => {
  if (typeof str != "string") return false
  return !isNaN(str) && !isNaN(parseFloat(str));
};

const clickStyle = `
  @keyframes blink-animation {
    50% {
    transform: scale(1.2);
    }
  }
`;

const disabledButtonStyle = {
  backgroundColor: colors.gray700,
  cursor: "not-allowed",
  pointerEvents: "auto",
  color: 'white',
  border: 'none'
};

const submittedButtonStyle = {
  backgroundColor: colors.blue500,
  color: 'white',
  cursor: "not-allowed",
};

const errorButtonStyle = {
  backgroundColor: "#DC3545",
  color: colors.milkGlass500,
};

const resubmittedButtonStyle = {
  backgroundColor: colors.blue500,
  color: colors.milkGlass500,
  opacity: 0.75,
};

const inputStyle = {
  width: "150px",
  height: "50px",
  borderRadius: "6px",
  border: `1px solid ${colors.milkGlass950}`,
  backgroundColor: "white",
  outline: "none",
  textAlign: "center"
};

const disabledInputStyle = {
  cursor: "not-allowed",
  border: "1px solid rgba(0, 0, 0, 0.125)",
  backgroundColor: colors.nightlyWoods100
};

const adornedAnswerBoxStyle = {
  display: "inline-flex",
  alignItems: "center",
  width: "auto",
  minWidth: "150px",
  height: "50px",
  borderRadius: "6px",
  border: `1px solid ${colors.milkGlass950}`,
  backgroundColor: "white",
  boxSizing: "border-box",
  padding: "0 12px",
  cursor: "text"
};

const disabledAdornedAnswerBoxStyle = {
  cursor: "not-allowed",
  border: "1px solid rgba(0, 0, 0, 0.125)",
  backgroundColor: colors.nightlyWoods100
};

const innerInputStyle = {
  border: "none",
  outline: "none",
  backgroundColor: "transparent",
  height: "100%",
  padding: 0,
  margin: 0,
  fontSize: "inherit",
  fontFamily: "inherit",
  color: "inherit",
  textAlign: "left",
  minWidth: "1ch",
  maxWidth: "100%"
};
